import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle,
  Circle,
  Loader2,
  AlertTriangle,
  ClipboardCheck,
  MapPin,
  Phone,
  Globe,
  Shield,
  Send,
  Play,
  Plus,
  Trash2,
  Settings,
  Package,
  XCircle,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import api, {
  Client,
  ClientOnboarding as OnboardingType,
  OnboardingModuleChecklist,
  ValidationResults,
  Market,
  Service,
  ClientService as ClientServiceType,
  TrackingNumber,
  ClientNotificationContact,
  LandingPageConfig,
} from '../services/api';


// ============================================================================
// STEP METADATA
// ============================================================================

const STEP_META = [
  { num: 1, name: 'Basics & Scope', icon: ClipboardCheck },
  { num: 2, name: 'Markets & Services', icon: MapPin },
  { num: 3, name: 'Lead Intake', icon: Phone },
  { num: 4, name: 'Estimate Page', icon: Globe },
  { num: 5, name: 'Modules', icon: Package },
  { num: 6, name: 'Validation', icon: Shield },
  { num: 7, name: 'Handoff', icon: Send },
];

const MODULE_LABELS: Record<string, string> = {
  quote_acceleration: 'Quote Acceleration',
  gbp_optimization: 'GBP Optimization',
  ads_setup: 'Ads Setup',
  referral_intake: 'Referral Intake',
  analytics: 'Analytics',
};

const CHECKLIST_LABELS: Record<string, string> = {
  // Step 1
  services_confirmed: 'Services to promote confirmed',
  industry_confirmed: 'Industry focus confirmed',
  modules_selected: 'Included modules selected',
  classification_set: 'Client classification set',
  // Step 2
  market_added: 'At least 1 market added',
  primary_service_selected: 'Primary service selected',
  // Step 3
  tracking_number_provisioned: 'Tracking number provisioned',
  call_forwarding_configured: 'Call forwarding configured',
  notification_contacts_set: 'Notification contacts set',
  test_call_passed: 'Test call / form logged',
  // Step 4
  landing_page_configured: 'Landing page config filled',
  subdomain_live: 'Subdomain live',
  form_submission_verified: 'Form submits to lead API',
  attribution_confirmed: 'Attribution captured',
  // Step 5 module items
  pricing_logic_captured: 'Pricing logic captured',
  quote_template_created: 'Quote template created',
  test_quote_sent: 'Test quote sent',
  profile_claimed: 'Profile claimed',
  categories_set: 'Categories set',
  hours_verified: 'Hours verified',
  photos_uploaded: 'Photos uploaded',
  account_linked: 'Account linked',
  campaign_created: 'Campaign created',
  conversion_tracking_set: 'Conversion tracking set',
  referral_tracking_number: 'Referral tracking number',
  referral_landing_page: 'Referral landing page',
  gsc_linked: 'GSC linked',
  reporting_configured: 'Reporting configured',
  // Step 6
  test_lead_received: 'Test lead received',
  auto_ack_under_60s: 'Auto-ack sent < 60s',
  owner_notified: 'Owner notified',
  quote_flow_verified: 'Quote flow usable',
  // Step 7
  handoff_email_sent: 'Handoff email sent',
  explanation_delivered: '"How it works" explanation delivered',
  client_confirmed_understanding: 'Client confirmed understanding',
};


// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ClientOnboarding = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();

  const [client, setClient] = useState<Client | null>(null);
  const [onboarding, setOnboarding] = useState<OnboardingType | null>(null);
  const [activeStep, setActiveStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Supporting data
  const [markets, setMarkets] = useState<Market[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [clientServices, setClientServices] = useState<ClientServiceType[]>([]);
  const [trackingNumbers, setTrackingNumbers] = useState<TrackingNumber[]>([]);
  const [notificationContacts, setNotificationContacts] = useState<ClientNotificationContact[]>([]);
  const [landingPages, setLandingPages] = useState<LandingPageConfig[]>([]);

  // Step-specific state
  const [validationResults, setValidationResults] = useState<ValidationResults | null>(null);
  const [testLeadResult, setTestLeadResult] = useState<string>('');
  const [handoffEmail, setHandoffEmail] = useState('');

  const loadData = useCallback(async () => {
    if (!clientId) return;
    try {
      setLoading(true);
      const [clientData, onboardingData, marketsData, servicesResp, clientServicesData] = await Promise.all([
        api.getClient(clientId),
        api.getClientOnboarding(clientId),
        api.getClientMarkets(clientId),
        api.getServices(),
        api.getClientServices(clientId),
      ]);
      setClient(clientData);
      setOnboarding(onboardingData);
      setMarkets(marketsData);
      setServices(servicesResp.results || []);
      setClientServices(clientServicesData);
      setActiveStep(onboardingData.current_step);
      setHandoffEmail(clientData.email || '');

      // Load additional data
      const [tnData, ncData, lpData] = await Promise.all([
        api.getTrackingNumbers(clientId),
        api.getNotificationContacts(clientId),
        api.getClientLandingPages(clientId),
      ]);
      setTrackingNumbers(tnData);
      setNotificationContacts(ncData);
      setLandingPages(lpData);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { loadData(); }, [loadData]);

  // ---- Handlers ----

  const handleChecklistChange = async (key: string, value: boolean) => {
    if (!clientId || !onboarding) return;
    setSaving(true);
    try {
      const updated = await api.updateOnboardingStep(clientId, activeStep, {
        checklist: { [key]: value },
      });
      setOnboarding(updated);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleModuleChecklistChange = async (
    moduleKey: string,
    itemKey: string,
    value: boolean
  ) => {
    if (!clientId || !onboarding) return;
    setSaving(true);
    try {
      const updated = await api.updateOnboardingStep(clientId, 5, {
        checklist: { [moduleKey]: { items: { [itemKey]: value } } },
      });
      setOnboarding(updated);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleCompleteStep = async () => {
    if (!clientId || !onboarding) return;
    setSaving(true);
    setError('');
    try {
      const updated = await api.updateOnboardingStep(clientId, activeStep, {
        status: 'complete',
      });
      setOnboarding(updated);
      if (activeStep < 7) {
        setActiveStep(activeStep + 1);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to complete step';
      // Try to parse JSON error from API
      try {
        const parsed = JSON.parse(message);
        setError(parsed.error || message);
      } catch {
        setError(message);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleModulesSoldChange = async (modules: string[]) => {
    if (!clientId) return;
    setSaving(true);
    try {
      const updated = await api.updateClientOnboarding(clientId, { modules_sold: modules });
      setOnboarding(updated);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update modules');
    } finally {
      setSaving(false);
    }
  };

  const handleClientUpdate = async (data: Partial<Client>) => {
    if (!clientId) return;
    setSaving(true);
    try {
      const updated = await api.updateClient(clientId, data);
      setClient(updated);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update client');
    } finally {
      setSaving(false);
    }
  };

  const handleRunTest = async () => {
    if (!clientId || !client) return;
    setSaving(true);
    setTestLeadResult('');
    try {
      const result = await api.createOnboardingTestLead(clientId, {
        name: 'Test Lead',
        phone: client.phone || '+10000000000',
        email: client.email || 'test@digimint.co',
      });
      setTestLeadResult(`Test lead created (${result.lead_id})`);
      // Refresh onboarding to pick up auto-computed checklist
      const updated = await api.updateOnboardingStep(clientId, activeStep, { checklist: {} });
      setOnboarding(updated);
    } catch (err: unknown) {
      setTestLeadResult(err instanceof Error ? err.message : 'Test failed');
    } finally {
      setSaving(false);
    }
  };

  const handleRunValidation = async () => {
    if (!clientId) return;
    setSaving(true);
    try {
      const results = await api.runOnboardingValidation(clientId);
      setValidationResults(results);
      // Refresh onboarding (step 6 checklist is auto-updated)
      const updated = await api.getClientOnboarding(clientId);
      setOnboarding(updated);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Validation failed');
    } finally {
      setSaving(false);
    }
  };

  const handleSendHandoff = async () => {
    if (!clientId || !handoffEmail) return;
    setSaving(true);
    try {
      await api.sendOnboardingHandoff(clientId, { recipient_email: handoffEmail });
      // Refresh onboarding state
      const updated = await api.getClientOnboarding(clientId);
      setOnboarding(updated);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to send handoff');
    } finally {
      setSaving(false);
    }
  };

  const handleCompleteOnboarding = async () => {
    if (!clientId) return;
    setSaving(true);
    setError('');
    try {
      // First complete step 7
      await api.updateOnboardingStep(clientId, 7, { status: 'complete' });
      // Then transition phase
      await api.transitionClientPhase(clientId);
      // Reload
      await loadData();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to complete';
      try {
        const parsed = JSON.parse(message);
        setError(parsed.error || message);
      } catch {
        setError(message);
      }
    } finally {
      setSaving(false);
    }
  };

  // ---- Render helpers ----

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="animate-spin" size={32} />
      </div>
    );
  }

  if (!client || !onboarding) {
    return (
      <div className="p-6">
        <p className="text-red-500">Failed to load onboarding data.</p>
        <Link to={`/clients/${clientId}`} className="text-blue-500 underline mt-2 inline-block">
          Back to client
        </Link>
      </div>
    );
  }

  const currentStepData = onboarding.steps[String(activeStep)];
  const stepStatus = currentStepData?.status || 'pending';
  const checklist = currentStepData?.checklist || {};
  const allChecked = activeStep === 5
    ? Object.values(checklist).every((mod) => {
        if (typeof mod === 'object' && mod !== null && 'items' in mod) {
          return Object.values((mod as OnboardingModuleChecklist).items).every(Boolean);
        }
        return true;
      })
    : Object.values(checklist).every((v) => v === true);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Link
          to={`/clients/${clientId}`}
          className="p-2 rounded-lg hover:bg-[var(--bg-secondary)] transition-colors"
        >
          <ArrowLeft size={20} />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{client.company} — Onboarding</h1>
          <p className="text-sm text-[var(--text-secondary)]">
            {onboarding.progress_percentage}% complete
          </p>
        </div>
        {onboarding.is_complete && (
          <span className="status-badge status-active">Complete</span>
        )}
      </div>

      {/* Progress bar */}
      <div className="w-full bg-[var(--bg-secondary)] rounded-full h-2 mb-6">
        <div
          className="bg-[var(--color-primary)] h-2 rounded-full transition-all duration-300"
          style={{ width: `${onboarding.progress_percentage}%` }}
        />
      </div>

      {/* Stepper */}
      <div className="flex items-center justify-between mb-8 overflow-x-auto pb-2">
        {STEP_META.map((step, idx) => {
          const sData = onboarding.steps[String(step.num)];
          const isComplete = sData?.status === 'complete';
          const isActive = step.num === activeStep;
          const Icon = step.icon;

          return (
            <div key={step.num} className="flex items-center">
              <button
                onClick={() => setActiveStep(step.num)}
                className={`
                  flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-all min-w-[80px]
                  ${isActive ? 'bg-[var(--color-primary)] bg-opacity-10 ring-2 ring-[var(--color-primary)]' : ''}
                  ${isComplete ? 'text-green-600 dark:text-green-400' : ''}
                  hover:bg-[var(--bg-secondary)]
                `}
              >
                <div className="relative">
                  {isComplete ? (
                    <CheckCircle size={24} className="text-green-500" />
                  ) : (
                    <Icon size={24} className={isActive ? 'text-[var(--color-primary)]' : 'text-[var(--text-secondary)]'} />
                  )}
                </div>
                <span className={`text-xs font-medium whitespace-nowrap ${isActive ? 'text-[var(--color-primary)]' : ''}`}>
                  {step.name}
                </span>
              </button>
              {idx < STEP_META.length - 1 && (
                <div className={`h-px w-6 mx-1 ${isComplete ? 'bg-green-500' : 'bg-[var(--border-primary)]'}`} />
              )}
            </div>
          );
        })}
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-500 bg-opacity-10 border border-red-500 text-red-600 dark:text-red-400 rounded-lg p-3 mb-4 flex items-center gap-2">
          <AlertTriangle size={16} />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto text-sm underline">Dismiss</button>
        </div>
      )}

      {/* Billing gate banner */}
      {activeStep >= 3 && client.billing_status !== 'paid' && client.billing_status !== 'subscription_active' && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg p-4 mb-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Payment Required</p>
            <p className="text-xs text-amber-600 dark:text-amber-400">
              Initial engagement must be paid before proceeding past Step 2.{' '}
              <Link to={`/clients/${clientId}`} className="underline font-medium">Go to Billing →</Link>
            </p>
          </div>
        </div>
      )}

      {/* Step content */}
      <div className="panel p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">
            Step {activeStep}: {STEP_META[activeStep - 1].name}
          </h2>
          {stepStatus === 'complete' && (
            <span className="text-green-500 flex items-center gap-1 text-sm">
              <CheckCircle size={16} /> Completed
            </span>
          )}
        </div>

        {/* Render step content */}
        {activeStep === 1 && (
          <Step1Content
            client={client}
            checklist={checklist as Record<string, boolean>}
            onCheck={handleChecklistChange}
            modulesSold={onboarding.modules_sold}
            onModulesChange={handleModulesSoldChange}
            onClientUpdate={handleClientUpdate}
            saving={saving}
          />
        )}
        {activeStep === 2 && (
          <Step2Content
            client={client}
            clientId={clientId!}
            checklist={checklist as Record<string, boolean>}
            onCheck={handleChecklistChange}
            markets={markets}
            services={services}
            clientServices={clientServices}
            onRefresh={loadData}
            saving={saving}
          />
        )}
        {activeStep === 3 && (
          <Step3Content
            client={client}
            clientId={clientId!}
            checklist={checklist as Record<string, boolean>}
            onCheck={handleChecklistChange}
            trackingNumbers={trackingNumbers}
            notificationContacts={notificationContacts}
            onRunTest={handleRunTest}
            onRefresh={loadData}
            testResult={testLeadResult}
            saving={saving}
          />
        )}
        {activeStep === 4 && (
          <Step4Content
            client={client}
            checklist={checklist as Record<string, boolean>}
            onCheck={handleChecklistChange}
            landingPages={landingPages}
            saving={saving}
          />
        )}
        {activeStep === 5 && (
          <Step5Content
            checklist={checklist}
            modulesSold={onboarding.modules_sold}
            onModuleCheck={handleModuleChecklistChange}
            saving={saving}
          />
        )}
        {activeStep === 6 && (
          <Step6Content
            checklist={checklist as Record<string, boolean>}
            validationResults={validationResults}
            onRunValidation={handleRunValidation}
            saving={saving}
          />
        )}
        {activeStep === 7 && (
          <Step7Content
            checklist={checklist as Record<string, boolean>}
            onCheck={handleChecklistChange}
            handoffEmail={handoffEmail}
            onHandoffEmailChange={setHandoffEmail}
            onSendHandoff={handleSendHandoff}
            onComplete={handleCompleteOnboarding}
            allComplete={onboarding.is_complete || allChecked}
            saving={saving}
          />
        )}
      </div>

      {/* Navigation + Complete */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => setActiveStep(Math.max(1, activeStep - 1))}
          disabled={activeStep === 1}
          className="btn btn-secondary flex items-center gap-1"
        >
          <ArrowLeft size={16} /> Previous
        </button>

        <div className="flex items-center gap-3">
          {stepStatus !== 'complete' && activeStep < 7 && (
            <button
              onClick={handleCompleteStep}
              disabled={saving || !allChecked || (activeStep >= 3 && client.billing_status !== 'paid' && client.billing_status !== 'subscription_active')}
              className="btn btn-primary flex items-center gap-1"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
              Complete Step {activeStep}
            </button>
          )}
          {activeStep < 7 && (
            <button
              onClick={() => setActiveStep(Math.min(7, activeStep + 1))}
              className="btn btn-secondary flex items-center gap-1"
            >
              Next <ArrowRight size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};


// ============================================================================
// STEP 1 — CLIENT BASICS & SCOPE
// ============================================================================

interface Step1Props {
  client: Client;
  checklist: Record<string, boolean>;
  onCheck: (key: string, value: boolean) => void;
  modulesSold: string[];
  onModulesChange: (modules: string[]) => void;
  onClientUpdate: (data: Partial<Client>) => Promise<void>;
  saving: boolean;
}

const GOOGLE_EXPOSURE_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'sab_single', label: 'SAB Single' },
  { value: 'sab_multi', label: 'SAB Multi' },
  { value: 'brand', label: 'Brand' },
];

const SCALE_PATTERN_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'single_market', label: 'Single Market' },
  { value: 'multi_market_finite', label: 'Multi-Market Finite' },
  { value: 'multi_market_programmatic', label: 'Multi-Market Programmatic' },
];

const DEMAND_MODE_OPTIONS = ['maps', 'organic', 'ai', 'ads'];

const Step1Content = ({ client, checklist, onCheck, modulesSold, onModulesChange, onClientUpdate, saving }: Step1Props) => {
  const allModules = ['quote_acceleration', 'gbp_optimization', 'ads_setup', 'referral_intake', 'analytics'];

  const toggleModule = (mod: string) => {
    const next = modulesSold.includes(mod)
      ? modulesSold.filter((m) => m !== mod)
      : [...modulesSold, mod];
    onModulesChange(next);
  };

  const handleExposureChange = (value: string) => {
    onClientUpdate({ google_exposure_model: value as Client['google_exposure_model'] });
  };

  const handleScaleChange = (value: string) => {
    onClientUpdate({ scale_pattern: value as Client['scale_pattern'] });
  };

  const handleDemandModeToggle = (mode: string) => {
    const current = client.demand_modes || [];
    const next = current.includes(mode as 'maps' | 'organic' | 'ai' | 'ads')
      ? current.filter((m) => m !== mode)
      : [...current, mode as 'maps' | 'organic' | 'ai' | 'ads'];
    onClientUpdate({ demand_modes: next });
  };

  return (
    <div className="space-y-6">
      {/* Client info summary */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-sm text-[var(--text-secondary)]">Company</p>
          <p className="font-medium">{client.company}</p>
        </div>
        <div>
          <p className="text-sm text-[var(--text-secondary)]">Industry</p>
          <p className="font-medium">{client.industry_display || client.industry_name || 'Not set'}</p>
        </div>
      </div>

      {/* Editable Classification Fields */}
      <div className="p-4 rounded-lg border border-[var(--border-primary)] bg-[var(--bg-secondary)]">
        <h3 className="font-medium mb-4">Client Classification</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm text-[var(--text-secondary)] mb-1">Google Exposure Model</label>
            <select
              value={client.google_exposure_model || ''}
              onChange={(e) => handleExposureChange(e.target.value)}
              disabled={saving}
              className="w-full px-3 py-2 rounded-lg border border-[var(--border-primary)] bg-[var(--bg-primary)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
            >
              {GOOGLE_EXPOSURE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-[var(--text-secondary)] mb-1">Scale Pattern</label>
            <select
              value={client.scale_pattern || ''}
              onChange={(e) => handleScaleChange(e.target.value)}
              disabled={saving}
              className="w-full px-3 py-2 rounded-lg border border-[var(--border-primary)] bg-[var(--bg-primary)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
            >
              {SCALE_PATTERN_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-[var(--text-secondary)] mb-1">Demand Modes</label>
            <div className="flex flex-wrap gap-2">
              {DEMAND_MODE_OPTIONS.map((mode) => {
                const isSelected = client.demand_modes?.includes(mode as 'maps' | 'organic' | 'ai' | 'ads') || false;
                return (
                  <label
                    key={mode}
                    className={`
                      flex items-center gap-2 px-3 py-1.5 rounded-lg border-2 cursor-pointer transition-all text-sm font-medium
                      ${isSelected
                        ? 'border-[var(--color-primary)] bg-[var(--color-primary)] text-black'
                        : 'border-[var(--border-primary)] hover:border-[var(--border-secondary)] bg-[var(--bg-primary)]'
                      }
                    `}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleDemandModeToggle(mode)}
                      disabled={saving}
                      className="sr-only"
                    />
                    {isSelected && <Check size={14} />}
                    {mode.charAt(0).toUpperCase() + mode.slice(1)}
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Module toggles */}
      <div>
        <h3 className="font-medium mb-3">Included Modules</h3>
        <div className="grid grid-cols-2 gap-2">
          {allModules.map((mod) => (
            <label
              key={mod}
              className={`
                flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all
                ${modulesSold.includes(mod)
                  ? 'border-[var(--color-primary)] bg-[var(--color-primary)] bg-opacity-5'
                  : 'border-[var(--border-primary)] hover:border-[var(--border-secondary)]'
                }
              `}
            >
              <input
                type="checkbox"
                checked={modulesSold.includes(mod)}
                onChange={() => toggleModule(mod)}
                disabled={saving}
                className="rounded"
              />
              <span className="text-sm font-medium">{MODULE_LABELS[mod]}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Checklist */}
      <div>
        <h3 className="font-medium mb-3">Confirmation Checklist</h3>
        <ChecklistGroup checklist={checklist} onCheck={onCheck} saving={saving} />
      </div>
    </div>
  );
};


// ============================================================================
// STEP 2 — MARKETS & SERVICES
// ============================================================================

interface Step2Props {
  client: Client;
  clientId: string;
  checklist: Record<string, boolean>;
  onCheck: (key: string, value: boolean) => void;
  markets: Market[];
  services: Service[];
  clientServices: ClientServiceType[];
  onRefresh: () => void;
  saving: boolean;
}

const Step2Content = ({ client, clientId, checklist, onCheck, markets, services, clientServices, onRefresh, saving }: Step2Props) => {
  const [selectedService, setSelectedService] = useState('');
  const [addingService, setAddingService] = useState(false);

  // Ensure arrays are valid
  const safeClientServices = Array.isArray(clientServices) ? clientServices : [];
  const safeServices = Array.isArray(services) ? services : [];

  // Filter out services that are already assigned
  const assignedServiceIds = safeClientServices.map(cs => cs.service);
  const availableServices = safeServices.filter(s => !assignedServiceIds.includes(s.id));

  const handleAddService = async () => {
    if (!selectedService) return;
    setAddingService(true);
    try {
      await api.createClientService(clientId, { service: selectedService, is_active: true });
      setSelectedService('');
      onRefresh();
    } catch (err) {
      console.error('Failed to add service:', err);
    } finally {
      setAddingService(false);
    }
  };

  const handleRemoveService = async (serviceId: string) => {
    try {
      await api.deleteClientService(clientId, serviceId);
      onRefresh();
    } catch (err) {
      console.error('Failed to remove service:', err);
    }
  };

  return (
    <div className="space-y-6">
      {/* Markets */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium">Markets ({markets.length})</h3>
          <Link
            to={`/clients/${clientId}`}
            className="text-sm text-[var(--color-primary)] hover:underline"
          >
            Manage in Client Detail
          </Link>
        </div>
        {markets.length === 0 ? (
          <p className="text-sm text-[var(--text-secondary)] italic">
            No markets added yet. Add markets from the Client Detail page.
          </p>
        ) : (
          <div className="space-y-2">
            {markets.map((m) => (
              <div key={m.id} className="flex items-center gap-2 p-2 rounded bg-[var(--bg-secondary)]">
                <MapPin size={14} className="text-[var(--text-secondary)]" />
                <span className="text-sm">{m.city}, {m.region}</span>
                {m.is_primary && <span className="text-xs bg-blue-500 bg-opacity-20 text-blue-600 dark:text-blue-400 px-2 py-0.5 rounded">Primary</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Services */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium">Active Services ({safeClientServices.filter((s) => s.is_active).length})</h3>
        </div>

        {/* Add Service Form */}
        <div className="flex gap-2 mb-3">
          <select
            value={selectedService}
            onChange={(e) => setSelectedService(e.target.value)}
            className="input text-sm flex-1"
            disabled={addingService}
          >
            <option value="">Select a service to add...</option>
            {availableServices.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.service_type})
              </option>
            ))}
          </select>
          <button
            onClick={handleAddService}
            disabled={!selectedService || addingService}
            className="btn btn-primary text-sm px-3"
          >
            {addingService ? 'Adding...' : 'Add'}
          </button>
        </div>

        {safeClientServices.length === 0 ? (
          <p className="text-sm text-[var(--text-secondary)] italic">
            No services assigned yet. Use the dropdown above to add services.
          </p>
        ) : (
          <div className="space-y-2">
            {safeClientServices.filter((s) => s.is_active).map((cs) => (
              <div key={cs.id} className="flex items-center justify-between gap-2 p-2 rounded bg-[var(--bg-secondary)]">
                <div className="flex items-center gap-2">
                  <Settings size={14} className="text-[var(--text-secondary)]" />
                  <span className="text-sm">{cs.service_name}</span>
                  <span className={`text-xs px-2 py-0.5 rounded ${cs.service_type === 'AI' ? 'bg-purple-500 bg-opacity-20 text-purple-600 dark:text-purple-400' : 'bg-green-500 bg-opacity-20 text-green-600 dark:text-green-400'}`}>
                    {cs.service_type}
                  </span>
                </div>
                <button
                  onClick={() => handleRemoveService(cs.id)}
                  className="text-red-500 hover:text-red-700 text-xs"
                  title="Remove service"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Gate info */}
      <div className={`p-3 rounded-lg text-sm ${
        markets.length >= 1 && safeClientServices.filter((s) => s.is_active).length >= 1
          ? 'bg-green-500 bg-opacity-10 text-green-700 dark:text-green-300'
          : 'bg-amber-500 bg-opacity-10 text-amber-700 dark:text-amber-300'
      }`}>
        {markets.length >= 1 && safeClientServices.filter((s) => s.is_active).length >= 1
          ? 'Gate passed: at least 1 market and 1 service exist.'
          : `Gate: Need ${markets.length < 1 ? '1+ market' : ''}${markets.length < 1 && safeClientServices.filter((s) => s.is_active).length < 1 ? ' and ' : ''}${safeClientServices.filter((s) => s.is_active).length < 1 ? '1+ active service' : ''}.`
        }
      </div>

      {/* Checklist */}
      <ChecklistGroup checklist={checklist} onCheck={onCheck} saving={saving} />
    </div>
  );
};


// ============================================================================
// STEP 3 — LEAD INTAKE SETUP
// ============================================================================

interface Step3Props {
  client: Client;
  clientId: string;
  checklist: Record<string, boolean>;
  onCheck: (key: string, value: boolean) => void;
  trackingNumbers: TrackingNumber[];
  notificationContacts: ClientNotificationContact[];
  onRunTest: () => void;
  onRefresh: () => void;
  testResult: string;
  saving: boolean;
}

const Step3Content = ({ client, clientId, checklist, onCheck, trackingNumbers, notificationContacts, onRunTest, onRefresh, testResult, saving }: Step3Props) => {
  const [testMode, setTestMode] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testModeLoading, setTestModeLoading] = useState(false);
  const [testModeError, setTestModeError] = useState('');

  // Load test mode status on mount
  useEffect(() => {
    const loadTestMode = async () => {
      try {
        const status = await api.getTestModeStatus(clientId);
        setTestMode(status.test_mode);
        if (status.test_phone) setTestPhone(status.test_phone);
      } catch (err) {
        console.error('Failed to load test mode status:', err);
      }
    };
    loadTestMode();
  }, [clientId]);

  const handleTestModeToggle = async () => {
    if (!testMode && !testPhone) {
      setTestModeError('Enter your phone number first');
      return;
    }
    setTestModeLoading(true);
    setTestModeError('');
    try {
      const result = await api.setTestMode(clientId, !testMode, testPhone);
      setTestMode(result.test_mode);
      onRefresh(); // Refresh to show updated forward numbers
    } catch (err) {
      setTestModeError('Failed to toggle test mode');
      console.error(err);
    } finally {
      setTestModeLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Test Mode Toggle */}
      <div className={`border rounded-lg p-4 ${testMode ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20' : 'border-[var(--border-primary)]'}`}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-medium flex items-center gap-2">
              🧪 Test Mode
              {testMode && <span className="text-xs bg-amber-500 text-white px-2 py-0.5 rounded">ACTIVE</span>}
            </h3>
            <p className="text-sm text-[var(--text-secondary)]">
              {testMode
                ? 'Calls and notifications are going to your test number'
                : 'Redirect calls and notifications to your number for testing'}
            </p>
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <input
            type="tel"
            value={testPhone}
            onChange={(e) => setTestPhone(e.target.value)}
            placeholder="Your phone (+19041234567)"
            className="input text-sm flex-1"
            disabled={testMode || testModeLoading}
          />
          <button
            onClick={handleTestModeToggle}
            disabled={testModeLoading}
            className={`btn text-sm px-4 ${testMode ? 'btn-secondary' : 'btn-primary'}`}
          >
            {testModeLoading ? 'Saving...' : testMode ? 'Disable Test Mode' : 'Enable Test Mode'}
          </button>
        </div>
        {testModeError && <p className="text-sm text-red-500 mt-2">{testModeError}</p>}
      </div>

      {/* Tracking numbers */}
      <div>
        <h3 className="font-medium mb-3">Tracking Numbers ({trackingNumbers.length})</h3>
        {trackingNumbers.length === 0 ? (
          <p className="text-sm text-[var(--text-secondary)] italic">No tracking numbers provisioned.</p>
        ) : (
          <div className="space-y-2">
            {trackingNumbers.map((tn) => (
              <div key={tn.id} className="flex items-center gap-3 p-2 rounded bg-[var(--bg-secondary)]">
                <Phone size={14} className="text-[var(--text-secondary)]" />
                <span className="text-sm font-mono">{tn.phone_display || tn.phone_e164}</span>
                <span className="text-xs text-[var(--text-secondary)]">→ {tn.forward_to || 'Not configured'}</span>
                <span className={`text-xs px-2 py-0.5 rounded ${tn.is_active ? 'bg-green-500 bg-opacity-20 text-green-600' : 'bg-red-500 bg-opacity-20 text-red-600'}`}>
                  {tn.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Notification contacts */}
      <div>
        <h3 className="font-medium mb-3">Notification Contacts ({notificationContacts.length})</h3>
        {notificationContacts.length === 0 ? (
          <p className="text-sm text-[var(--text-secondary)] italic">No notification contacts set.</p>
        ) : (
          <div className="space-y-2">
            {notificationContacts.map((nc) => (
              <div key={nc.id} className="flex items-center gap-3 p-2 rounded bg-[var(--bg-secondary)]">
                <span className="text-sm font-medium">{nc.name}</span>
                <span className="text-xs text-[var(--text-secondary)]">{nc.notify_via}</span>
                {nc.phone && <span className="text-xs font-mono">{nc.phone}</span>}
                {nc.email && <span className="text-xs">{nc.email}</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Run Test */}
      <div className="border border-[var(--border-primary)] rounded-lg p-4">
        <h3 className="font-medium mb-2">Test Lead Intake</h3>
        <p className="text-sm text-[var(--text-secondary)] mb-3">
          Creates a test lead through the real pipeline to verify tracking numbers, notifications, and auto-ack.
        </p>
        <button
          onClick={onRunTest}
          disabled={saving}
          className="btn btn-primary flex items-center gap-1"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />}
          Run Test
        </button>
        {testResult && (
          <p className="text-sm mt-2 text-green-600 dark:text-green-400">{testResult}</p>
        )}
      </div>

      {/* Checklist */}
      <ChecklistGroup checklist={checklist} onCheck={onCheck} saving={saving} />
    </div>
  );
};


// ============================================================================
// STEP 4 — CONVERSION / ESTIMATE PAGE
// ============================================================================

interface Step4Props {
  client: Client;
  checklist: Record<string, boolean>;
  onCheck: (key: string, value: boolean) => void;
  landingPages: LandingPageConfig[];
  saving: boolean;
}

const Step4Content = ({ client, checklist, onCheck, landingPages, saving }: Step4Props) => {
  return (
    <div className="space-y-6">
      {/* Landing pages */}
      <div>
        <h3 className="font-medium mb-3">Landing Pages ({landingPages.length})</h3>
        {landingPages.length === 0 ? (
          <p className="text-sm text-[var(--text-secondary)] italic">
            No landing pages configured. Create one from the Landing Pages section.
          </p>
        ) : (
          <div className="space-y-3">
            {landingPages.map((lp) => (
              <div key={lp.id} className="p-3 rounded-lg border border-[var(--border-primary)]">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">{lp.business_name}</span>
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    lp.build_status === 'success'
                      ? 'bg-green-500 bg-opacity-20 text-green-600 dark:text-green-400'
                      : lp.build_status === 'failed'
                      ? 'bg-red-500 bg-opacity-20 text-red-600 dark:text-red-400'
                      : 'bg-amber-500 bg-opacity-20 text-amber-600 dark:text-amber-400'
                  }`}>
                    {lp.build_status}
                  </span>
                </div>
                <p className="text-sm text-[var(--text-secondary)]">
                  <Globe size={12} className="inline mr-1" />
                  {lp.subdomain}.digimint.co
                </p>
                {lp.is_published && (
                  <p className="text-xs text-green-600 dark:text-green-400 mt-1">Published</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Checklist */}
      <ChecklistGroup checklist={checklist} onCheck={onCheck} saving={saving} />
    </div>
  );
};


// ============================================================================
// STEP 5 — OPTIONAL MODULES
// ============================================================================

interface Step5Props {
  checklist: Record<string, boolean | OnboardingModuleChecklist>;
  modulesSold: string[];
  onModuleCheck: (moduleKey: string, itemKey: string, value: boolean) => void;
  saving: boolean;
}

const Step5Content = ({ checklist, modulesSold, onModuleCheck, saving }: Step5Props) => {
  const [expandedModules, setExpandedModules] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    modulesSold.forEach((m) => { init[m] = true; });
    return init;
  });

  if (modulesSold.length === 0) {
    return (
      <div className="text-center py-8 text-[var(--text-secondary)]">
        <Package size={32} className="mx-auto mb-2 opacity-50" />
        <p>No optional modules selected.</p>
        <p className="text-sm mt-1">Go back to Step 1 to enable modules.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {modulesSold.map((moduleKey) => {
        const moduleData = checklist[moduleKey];
        if (!moduleData || typeof moduleData === 'boolean') return null;
        const mod = moduleData as OnboardingModuleChecklist;
        const items = mod.items || {};
        const allDone = Object.values(items).every(Boolean);
        const doneCount = Object.values(items).filter(Boolean).length;
        const isExpanded = expandedModules[moduleKey] !== false;

        return (
          <div key={moduleKey} className="border border-[var(--border-primary)] rounded-lg overflow-hidden">
            <button
              onClick={() => setExpandedModules((prev) => ({ ...prev, [moduleKey]: !isExpanded }))}
              className="w-full flex items-center justify-between p-4 hover:bg-[var(--bg-secondary)] transition-colors"
            >
              <div className="flex items-center gap-3">
                {allDone ? (
                  <CheckCircle size={20} className="text-green-500" />
                ) : (
                  <Circle size={20} className="text-[var(--text-secondary)]" />
                )}
                <span className="font-medium">{mod.name || MODULE_LABELS[moduleKey]}</span>
                <span className="text-xs text-[var(--text-secondary)]">
                  {doneCount}/{Object.keys(items).length}
                </span>
              </div>
              {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
            {isExpanded && (
              <div className="px-4 pb-4 space-y-2">
                {Object.entries(items).map(([itemKey, itemValue]) => (
                  <label
                    key={itemKey}
                    className="flex items-center gap-3 p-2 rounded hover:bg-[var(--bg-secondary)] cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={itemValue}
                      onChange={(e) => onModuleCheck(moduleKey, itemKey, e.target.checked)}
                      disabled={saving}
                      className="rounded"
                    />
                    <span className="text-sm">{CHECKLIST_LABELS[itemKey] || itemKey.replace(/_/g, ' ')}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};


// ============================================================================
// STEP 6 — VALIDATION
// ============================================================================

interface Step6Props {
  checklist: Record<string, boolean>;
  validationResults: ValidationResults | null;
  onRunValidation: () => void;
  saving: boolean;
}

const Step6Content = ({ checklist, validationResults, onRunValidation, saving }: Step6Props) => {
  return (
    <div className="space-y-6">
      <div className="bg-amber-500 bg-opacity-10 border border-amber-500 text-amber-700 dark:text-amber-300 rounded-lg p-3 text-sm">
        This step is non-skippable. All validation checks must pass before proceeding.
      </div>

      <button
        onClick={onRunValidation}
        disabled={saving}
        className="btn btn-primary flex items-center gap-1"
      >
        {saving ? <Loader2 size={16} className="animate-spin" /> : <Shield size={16} />}
        Run Validation
      </button>

      {validationResults && (
        <div className="space-y-3">
          <div className={`p-3 rounded-lg text-sm font-medium ${
            validationResults.all_passed
              ? 'bg-green-500 bg-opacity-10 text-green-700 dark:text-green-300'
              : 'bg-red-500 bg-opacity-10 text-red-700 dark:text-red-300'
          }`}>
            {validationResults.all_passed ? 'All checks passed!' : 'Some checks failed.'}
          </div>

          {Object.entries(validationResults.checks).map(([key, result]) => (
            <div key={key} className="flex items-start gap-3 p-3 rounded bg-[var(--bg-secondary)]">
              {result.passed ? (
                <CheckCircle size={18} className="text-green-500 mt-0.5" />
              ) : (
                <XCircle size={18} className="text-red-500 mt-0.5" />
              )}
              <div>
                <p className="text-sm font-medium">{CHECKLIST_LABELS[key] || key.replace(/_/g, ' ')}</p>
                <p className="text-xs text-[var(--text-secondary)]">{result.detail}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {!validationResults && (
        <div className="text-sm text-[var(--text-secondary)]">
          <p>Current checklist status:</p>
          <div className="mt-2 space-y-1">
            {Object.entries(checklist).map(([key, value]) => (
              <div key={key} className="flex items-center gap-2">
                {value ? <CheckCircle size={14} className="text-green-500" /> : <Circle size={14} className="text-[var(--text-secondary)]" />}
                <span>{CHECKLIST_LABELS[key] || key.replace(/_/g, ' ')}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};


// ============================================================================
// STEP 7 — HANDOFF
// ============================================================================

interface Step7Props {
  checklist: Record<string, boolean>;
  onCheck: (key: string, value: boolean) => void;
  handoffEmail: string;
  onHandoffEmailChange: (v: string) => void;
  onSendHandoff: () => void;
  onComplete: () => void;
  allComplete: boolean;
  saving: boolean;
}

const Step7Content = ({ checklist, onCheck, handoffEmail, onHandoffEmailChange, onSendHandoff, onComplete, allComplete, saving }: Step7Props) => {
  return (
    <div className="space-y-6">
      {/* Send handoff email */}
      <div className="border border-[var(--border-primary)] rounded-lg p-4">
        <h3 className="font-medium mb-2">Handoff Email</h3>
        <p className="text-sm text-[var(--text-secondary)] mb-3">
          Send the welcome email with a summary of what was set up.
        </p>
        <div className="flex gap-2">
          <input
            type="email"
            value={handoffEmail}
            onChange={(e) => onHandoffEmailChange(e.target.value)}
            placeholder="client@example.com"
            className="input flex-1"
          />
          <button
            onClick={onSendHandoff}
            disabled={saving || !handoffEmail || checklist.handoff_email_sent}
            className="btn btn-primary flex items-center gap-1"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {checklist.handoff_email_sent ? 'Sent' : 'Send'}
          </button>
        </div>
        {checklist.handoff_email_sent && (
          <p className="text-xs text-green-600 dark:text-green-400 mt-2">Handoff email has been sent.</p>
        )}
      </div>

      {/* Manual confirmations */}
      <div>
        <h3 className="font-medium mb-3">Confirmations</h3>
        <div className="space-y-2">
          <label className="flex items-center gap-3 p-3 rounded-lg border border-[var(--border-primary)] cursor-pointer hover:bg-[var(--bg-secondary)]">
            <input
              type="checkbox"
              checked={checklist.explanation_delivered || false}
              onChange={(e) => onCheck('explanation_delivered', e.target.checked)}
              disabled={saving}
              className="rounded"
            />
            <span className="text-sm">"How it works" explanation delivered to client</span>
          </label>
          <label className="flex items-center gap-3 p-3 rounded-lg border border-[var(--border-primary)] cursor-pointer hover:bg-[var(--bg-secondary)]">
            <input
              type="checkbox"
              checked={checklist.client_confirmed_understanding || false}
              onChange={(e) => onCheck('client_confirmed_understanding', e.target.checked)}
              disabled={saving}
              className="rounded"
            />
            <span className="text-sm">Client confirmed understanding</span>
          </label>
        </div>
      </div>

      {/* Complete & transition */}
      <div className="border-t border-[var(--border-primary)] pt-4">
        <button
          onClick={onComplete}
          disabled={saving || !allComplete}
          className="btn btn-primary w-full py-3 text-base flex items-center justify-center gap-2"
        >
          {saving ? <Loader2 size={20} className="animate-spin" /> : <CheckCircle size={20} />}
          Complete Onboarding & Transition to Cruising
        </button>
        {!allComplete && (
          <p className="text-xs text-[var(--text-secondary)] text-center mt-2">
            All checklist items must be completed first.
          </p>
        )}
      </div>
    </div>
  );
};


// ============================================================================
// SHARED COMPONENTS
// ============================================================================

interface ChecklistGroupProps {
  checklist: Record<string, boolean>;
  onCheck: (key: string, value: boolean) => void;
  saving: boolean;
}

const ChecklistGroup = ({ checklist, onCheck, saving }: ChecklistGroupProps) => (
  <div className="space-y-2">
    {Object.entries(checklist).map(([key, value]) => (
      <label
        key={key}
        className="flex items-center gap-3 p-3 rounded-lg border border-[var(--border-primary)] cursor-pointer hover:bg-[var(--bg-secondary)] transition-colors"
      >
        <input
          type="checkbox"
          checked={value}
          onChange={(e) => onCheck(key, e.target.checked)}
          disabled={saving}
          className="rounded"
        />
        <span className="text-sm">{CHECKLIST_LABELS[key] || key.replace(/_/g, ' ')}</span>
        {value && <CheckCircle size={14} className="ml-auto text-green-500" />}
      </label>
    ))}
  </div>
);


export default ClientOnboarding;
